import React, { useCallback, useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

export default function AdminUsersPage() {
  const { token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/users', {
        headers: { 'x-auth-token': token },
      });
      if (res.ok) setUsers(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRoleChange = async (userId, newRole, targetEmail) => {
    if (targetEmail.toLowerCase() === 'lakshaychawla13@gmail.com') {
      alert('Action Forbidden: Super Admin owner account cannot be demoted or modified.');
      return;
    }

    try {
      const res = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.msg || 'Role change failed.');
      }

      setToastMessage('User role updated successfully! 🎉');
      setTimeout(() => setToastMessage(''), 3000);
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (targetUser.email.toLowerCase() === 'lakshaychawla13@gmail.com') {
      alert('Action Forbidden: Super Admin owner account cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete ${targetUser.email}? Their account and custom-order conversations will be removed.`)) return;

    try {
      const res = await fetch(`/api/users/${targetUser.id}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Account deletion failed.');
      setToastMessage('User account deleted successfully.');
      setTimeout(() => setToastMessage(''), 3000);
      loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="brand-font mb-4">User & Role Management</h2>

      {toastMessage && (
        <div className="alert alert-success py-2 px-3 small text-center mb-3 fade show shadow-sm">
          {toastMessage}
        </div>
      )}

      <div className="border rounded bg-card p-3 shadow-sm">
        {loading ? (
          <div className="spinner-border text-primary" role="status"></div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isOwner = u.email.toLowerCase() === 'lakshaychawla13@gmail.com';

                  return (
                    <tr key={u.id} className={isOwner ? 'table-warning bg-opacity-25' : ''}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={u.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email}`}
                            alt=""
                            className="rounded-circle"
                            style={{ width: '36px', height: '36px' }}
                          />
                          <span className="font-weight-bold small">{u.name || 'User'}</span>
                        </div>
                      </td>
                      <td className="small">{u.email}</td>
                      <td>
                        <span className={`badge ${isOwner ? 'bg-warning text-dark' : 'bg-primary'}`}>
                          {isOwner ? '👑 SUPER_ADMIN' : u.role.toUpperCase()}
                        </span>
                      </td>
                      <td className="small">
                        {isOwner ? (
                          <span className="badge bg-danger">IMMUTABLE OWNER</span>
                        ) : (
                          <span className="badge bg-success">Active</span>
                        )}
                      </td>
                      <td>
                        {isOwner ? (
                          <span className="text-muted small font-italic">Protected Owner Account</span>
                        ) : (
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <select
                              className="form-select form-select-sm"
                              value={u.role}
                              onChange={(e) => handleRoleChange(u.id, e.target.value, u.email)}
                              style={{ width: '150px' }}
                            >
                              <option value="customer">Customer</option>
                              <option value="editor">Editor</option>
                              <option value="admin">Admin</option>
                              <option value="superadmin">Super Admin</option>
                            </select>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteUser(u)}>
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
